/**
 * LIBRARY
 * - 検索（タイトル・場所・天気・メモ・学んだこと・現像メモ）
 * - チップ：すべて / N / N+1 / N+2 / N−1 / N−2 / 振り返り待ち
 * - 行：サムネイル、題名、3 段の進捗、機材、相対時刻 · spots · 方針、天気・太陽高度
 * - 行の … → 地図で開く（位置情報があるとき）／削除（確認後、行が畳まれて消える）
 * - 行をタップでシーンノート
 */

import {
  showScreen, escapeHtml, toast, icon, formatRelative, openActionSheet, confirmDialog,
  blobUrl, releaseUrls, barHtml, backBtn,
} from './ui.js';
import { listShoots, deleteShoot, searchShoots } from './store.js';
import { getFilmById } from './data/films.js';
import { planLabel } from './zone-calc.js';

const FILTERS = [['all', 'すべて'], ['N', 'N'], ['N+1', 'N+1'], ['N+2', 'N+2'], ['N-1', 'N−1'], ['N-2', 'N−2'], ['pending', '振り返り待ち']];
let filter = 'all';
let query = '';
let total = 0;

export async function openLibrary(opts = {}) {
  filter = opts.filter || 'all';
  query = '';
  renderShell();
  showScreen('library');
  await renderList();
}

function renderShell() {
  const el = document.getElementById('screen-library');
  el.innerHTML = `
    ${barHtml(backBtn())}
    <div class="page-head"><div><div class="eyebrow">library</div><div class="page-title">ライブラリ</div></div><div class="big-count" id="lib-count">—<small>シーン</small></div></div>
    <label class="search" id="lib-search">${icon('search', 18)}<input type="search" placeholder="タイトル・場所・メモを検索" autocomplete="off" enterkeyhint="search" /></label>
    <div class="chips" id="lib-chips"></div>
    <div class="lib-cols">予測 · 現像 · プリント</div>
    <div class="body" id="lib-body"></div>
  `;
  const input = el.querySelector('#lib-search input');
  let t = null;
  input.addEventListener('input', () => {
    query = input.value;
    el.querySelector('#lib-search').classList.toggle('is-active', !!query.trim());
    clearTimeout(t);
    t = setTimeout(renderList, 120);
  });
  paintChips();
}

function paintChips() {
  const box = document.getElementById('lib-chips');
  box.innerHTML = FILTERS.map(([v, t]) => `<button type="button" class="chip ${filter === v ? 'is-on' : ''}" data-f="${v}">${t}</button>`).join('');
  box.querySelectorAll('[data-f]').forEach((b) => b.addEventListener('click', () => {
    filter = b.dataset.f;
    paintChips();
    renderList();
  }));
}

async function renderList() {
  const body = document.getElementById('lib-body');
  releaseUrls('library');
  let shoots = [];
  try {
    const all = await listShoots({ limit: 200 });
    total = all.length;
    shoots = query.trim() ? await searchShoots(query, 200) : all;
  } catch (e) {
    console.warn('[library] load failed:', e);
  }
  if (filter === 'pending') shoots = shoots.filter((s) => !s.development || !s.printing);
  else if (filter !== 'all') shoots = shoots.filter((s) => s.developmentPlan === filter);
  document.getElementById('lib-count').innerHTML = `${total}<small>シーン</small>`;

  if (total === 0) {
    body.innerHTML = `<div class="empty"><div class="empty__t">まだシーンがありません</div><p class="empty__d">ホームの「新規シーンを撮影」から始めましょう。</p></div>`;
    return;
  }
  if (shoots.length === 0) {
    body.innerHTML = `<div class="empty"><div class="empty__t">該当するシーンがありません</div><p class="empty__d">検索語や絞り込みを変えてみてください。</p><button class="btn-line" type="button" data-clear style="margin-top:14px">絞り込みを解除</button></div>`;
    body.querySelector('[data-clear]').addEventListener('click', () => {
      filter = 'all';
      query = '';
      const input = document.querySelector('#lib-search input');
      if (input) input.value = '';
      document.getElementById('lib-search').classList.remove('is-active');
      paintChips();
      renderList();
    });
    return;
  }

  body.innerHTML = shoots.map((s, i) => {
    const film = getFilmById(s.filmId)?.name || s.filmId || '?';
    const env = s.environment || {};
    const envBits = [];
    if (env.weather?.description) envBits.push(`${env.weather.description}${env.weather.tempC != null ? ` ${Math.round(env.weather.tempC)}°C` : ''}`);
    if (env.sunAltitude != null) envBits.push(`太陽高度 ${Math.round(env.sunAltitude)}°`);
    const dots = [!!(s.spots && s.spots.length), !!s.development, !!s.printing];
    return `
      <div class="lrow-wrap" data-wrap="${escapeHtml(s.id)}"><div>
        <div class="lrow" data-open="${escapeHtml(s.id)}" role="button" tabindex="0" style="animation-delay:${Math.min(i, 10) * 50}ms">
          <div class="lrow__thumb thumb">${s.monoImageBlob ? `<img src="${blobUrl('library', s.monoImageBlob)}" alt="" />` : ''}</div>
          <div class="lrow__body">
            <div style="display:flex;align-items:center;gap:8px"><span class="lrow__t">${escapeHtml(s.title || '無題')}</span><span class="dots3">${dots.map((d) => `<span class="${d ? 'is-on' : ''}"></span>`).join('')}</span></div>
            <div class="lrow__l sub" style="margin-top:3px">${escapeHtml(`${s.camera || 'Camera'} · ${film} · EI ${s.ei}`)}</div>
            <div class="lrow__l" style="margin-top:1px">${escapeHtml(`${formatRelative(s.updatedAt || s.createdAt)} · ${s.spots?.length || 0} spots · ${s.developmentPlan ? planLabel(s.developmentPlan) : '未計算'}`)}</div>
            ${envBits.length ? `<div class="sub" style="font-size:11px;line-height:1.5;margin-top:1px">${escapeHtml(envBits.join(' · '))}</div>` : ''}
          </div>
          <button class="bar__btn" type="button" data-more="${escapeHtml(s.id)}" aria-label="その他の操作">${icon('dots', 18)}</button>
        </div>
      </div></div>`;
  }).join('');

  body.querySelectorAll('[data-open]').forEach((row) => row.addEventListener('click', (e) => {
    if (e.target.closest('[data-more]')) return;
    import('./reflection.js').then((m) => m.openShootById(row.dataset.open));
  }));
  body.querySelectorAll('[data-more]').forEach((b) => b.addEventListener('click', (e) => {
    e.stopPropagation();
    const s = shoots.find((x) => x.id === b.dataset.more);
    if (!s) return;
    const actions = [];
    if (s.gps && Number.isFinite(s.gps.lat)) {
      actions.push({ label: '地図で開く', icon: 'map', run: () => import('./reflection.js').then((m) => m.openMap(s.gps)) });
    }
    actions.push({
      label: 'シーンを削除…',
      icon: 'trash',
      run: async () => {
        const ok = await confirmDialog({ title: 'このシーンを削除しますか？', body: '写真・測光ピン・現像とプリントの記録がすべて消えます。この操作は取り消せません。', ok: '削除' });
        if (!ok) return;
        await deleteShoot(s.id);
        const wrap = document.querySelector(`[data-wrap="${CSS.escape(s.id)}"]`);
        wrap?.classList.add('is-gone');
        toast('シーンを削除しました');
        setTimeout(renderList, 420);
      },
    });
    openActionSheet({ title: s.title || '無題', meta: `${formatRelative(s.updatedAt || s.createdAt)} · ${s.spots?.length || 0} spots · ${s.developmentPlan ? planLabel(s.developmentPlan) : '未計算'}`, actions });
  }));
}
